import React, { useState, useRef, useEffect } from 'react';
import {
  Monitor,
  Smartphone,
  Tablet,
  Gamepad2,
  Ruler,
  ChevronDown,
  RotateCw,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';
import {
  DEVICE_CATEGORIES,
  getPresetsByCategory,
  getPresetById,
} from '../../utils/device-presets';
import { DeviceCategory } from '../../../core/types/live-preview.types';

const categoryIcons: Record<DeviceCategory, React.ReactNode> = {
  mobile: <Smartphone size={14} />,
  tablet: <Tablet size={14} />,
  desktop: <Monitor size={14} />,
  console: <Gamepad2 size={14} />,
  custom: <Ruler size={14} />,
};

interface DeviceSelectorProps {
  compact?: boolean;
}

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({ compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<DeviceCategory>('desktop');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    viewport,
    setDevicePreset,
    setCustomResolution,
    toggleOrientation,
  } = useLivePreviewStore();

  const selectedPreset = viewport.devicePresetId
    ? getPresetById(viewport.devicePresetId)
    : null;

  const displayName = compact
    ? `${viewport.customWidth}×${viewport.customHeight}`
    : selectedPreset
      ? selectedPreset.name
      : `${viewport.customWidth} x ${viewport.customHeight}`;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const presets = getPresetsByCategory(selectedCategory);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 py-1.5 rounded',
          'bg-zinc-700 hover:bg-zinc-600 text-sm',
          'border border-zinc-600',
          compact ? 'px-2 min-w-[100px]' : 'px-3 min-w-[180px]'
        )}
      >
        {selectedPreset ? (
          categoryIcons[selectedPreset.category]
        ) : (
          <Ruler size={14} />
        )}
        <span className="flex-1 text-left truncate text-xs">{displayName}</span>
        <ChevronDown size={14} className={cn('transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className={cn(
          "absolute top-full left-0 mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden",
          compact ? "w-[280px]" : "w-[320px]"
        )}>
          {/* Category tabs */}
          <div className="flex border-b border-zinc-700">
            {DEVICE_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={cn(
                  'flex-1 px-2 py-2 text-xs flex flex-col items-center gap-1',
                  'hover:bg-zinc-700 transition-colors',
                  selectedCategory === cat.id && 'bg-zinc-700 text-blue-400'
                )}
              >
                {categoryIcons[cat.id]}
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Device list */}
          <div className="max-h-[300px] overflow-y-auto">
            {selectedCategory === 'custom' ? (
              <div className="p-3 space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-zinc-400 mb-1 block">Width</label>
                    <input
                      type="number"
                      value={viewport.customWidth}
                      onChange={(e) =>
                        setCustomResolution(
                          parseInt(e.target.value) || 720,
                          viewport.customHeight
                        )
                      }
                      className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm"
                      min={1}
                      max={7680}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-zinc-400 mb-1 block">Height</label>
                    <input
                      type="number"
                      value={viewport.customHeight}
                      onChange={(e) =>
                        setCustomResolution(
                          viewport.customWidth,
                          parseInt(e.target.value) || 1560
                        )
                      }
                      className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm"
                      min={1}
                      max={4320}
                    />
                  </div>
                </div>
                <button
                  onClick={() => {
                    setDevicePreset(null);
                    setIsOpen(false);
                  }}
                  className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded text-sm"
                >
                  Apply Custom
                </button>
              </div>
            ) : (
              presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => {
                    setDevicePreset(preset.id);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'w-full px-3 py-2 flex items-center gap-3 hover:bg-zinc-700',
                    'text-left text-sm',
                    viewport.devicePresetId === preset.id && 'bg-zinc-700 text-blue-400'
                  )}
                >
                  {categoryIcons[preset.category]}
                  <div className="flex-1">
                    <div className="font-medium">{preset.name}</div>
                    <div className="text-xs text-zinc-400">
                      {preset.width} x {preset.height}
                      {preset.pixelRatio > 1 && ` @${preset.pixelRatio}x`}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Orientation toggle */}
          <div className="border-t border-zinc-700 p-2">
            <button
              onClick={toggleOrientation}
              className="w-full px-3 py-2 flex items-center justify-center gap-2 hover:bg-zinc-700 rounded text-sm"
            >
              <RotateCw size={14} />
              <span>
                {viewport.isPortrait ? 'Switch to Landscape' : 'Switch to Portrait'}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
